import React, { useState, useEffect } from "react";
import { fetchProfile } from "../../../Services/profile/profileservice";
import { createEquifax } from "../../../Services/EquifaxCreditReport/EquifaxCreditReportService";
import { fetchStateService } from "../../../Services/EquifaxCScore/equifax";
import GaugeChart from "react-gauge-chart";
import { showSuccessToast, showErrorToast } from "../../../utils/toastUtils";
import Loader from "../../../Loader";
import EquifaxReport from "./EquifaxReport";
import { dummyReport, dummyReport1 } from "./data";

const EquifaxForm = () => {
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [creditScore, setCreditScore] = useState(0);
  const [showReport, setShowReport] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [open, setOpend] = useState(false);
  const [normalizedScore, setNormalizedScore] = useState();
  const maxScore = 900;
  const GENDER_OPTIONS = [
    { label: "MALE", value: "male" },
    { label: "FEMALE", value: "female" },
  ];

  const initialForm = {
    api_id: "",
    api_key: "",
    token_id: "",
    name: "",
    mobile: "",
    pan: "",
    gender: "",
    dob: "",
    address: "",
    state: "",
    pincode: "",
    consent: "Y",
  };
  const [formData, setFormData] = useState(initialForm);
  const [states, setStates] = useState([]);

  useEffect(() => {
    const loadStates = async () => {
      try {
        const res = await fetchStateService();

        if (res?.data) {
          const stateArray = (res?.data || []).map((s) => ({
            label: s.state_name.toUpperCase(),
            value: s.abbreviation,
          }));
          setStates(stateArray);
        }
      } catch (err) {
        console.error("Failed to load states", err);
      }
    };
    loadStates();
  }, []);

  useEffect(() => {
    const loadProfile = async () => {
      try {
        const res = await fetchProfile();
        const p = res?.data;

        setFormData((prev) => ({
          ...prev,
          api_id: p?.user_id || "",
          api_key: p?.api_key || "",
          token_id: p?.token_id || "",
        }));
      } catch (e) {
        console.error(e);
      }
    };

    loadProfile();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "pan") {
      const panValue = value.toUpperCase().replace(/[^A-Z0-9]/g, "");

      if (panValue.length <= 10) {
        setFormData({ ...formData, pan: panValue });
        setErrors({ ...errors, pan: "" });
      }
      return;
    }

    if (name === "pincode") {
      const pinValue = value.replace(/\D/g, "");
      if (pinValue.length <= 6) {
        setFormData({ ...formData, pincode: pinValue });
        setErrors({ ...errors, pincode: "" });
      }
      return;
    }

    setFormData({ ...formData, [name]: value });
    setErrors({ ...errors, [name]: "" });
  };

  const isPanValid = /^[A-Z]{5}[0-9]{4}[A-Z]$/.test(formData.pan);

  const isPincodeValid = /^\d{6}$/.test(formData.pincode);

  const isMobileValid = /^\d{10}$/.test(formData.mobile);

  const isFormValid = () => {
    return (
      formData.name &&
      isMobileValid &&
      isPanValid &&
      formData.gender &&
      formData.dob &&
      formData.address &&
      formData.state &&
      isPincodeValid &&
      ["Y", "N"].includes(formData.consent)
    );
  };
  const isValidDate = (dateString) => {
    if (!dateString) return false;

    // Check if the date is in YYYY-MM-DD format (HTML date input format)
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(dateString)) return false;

    // Parse the date and check if it's valid
    const date = new Date(dateString);
    const timestamp = date.getTime();

    // Check if the date is invalid
    if (typeof timestamp !== "number" || Number.isNaN(timestamp)) {
      return false;
    }

    // Check if the date is not in the future
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (date > today) {
      return false;
    }

    // Check if the date is reasonable (not too old, e.g., before 1900)
    const minDate = new Date("1900-01-01");
    if (date < minDate) {
      return false;
    }

    // Check if the parsed date matches the input (prevents invalid dates like 2023-02-30)
    const [year, month, day] = dateString.split("-").map(Number);
    if (
      date.getFullYear() !== year ||
      date.getMonth() + 1 !== month ||
      date.getDate() !== day
    ) {
      return false;
    }

    return true;
  };

  /* Validation */
  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = "Name is required";
    if (!/^\d{10}$/.test(formData.mobile))
      newErrors.mobile = "Mobile must be 10 digits";
    if (!/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(formData.pan))
      newErrors.pan = "PAN format is incorrect (ABCDE1234F)";
    if (!/^\d{6}$/.test(formData.pincode))
      newErrors.pincode = "Pincode must be exactly 6 digits";

    if (!formData.gender) newErrors.gender = "Gender is required";
    if (formData?.dob === "") {
      newErrors.dob = "Date of Birth is required.";
    } else if (!isValidDate(formData?.dob)) {
      newErrors.dob = "Enter a valid date of birth.";
    }
    if (!formData.address.trim()) newErrors.address = "Address is required";
    if (!formData.state.trim()) newErrors.state = "State is required";
    if (!/^\d{6}$/.test(formData.pincode))
      newErrors.pincode = "Pincode must be 6 digits";
    if (!["Y", "N"].includes(formData.consent)) {
      newErrors.consent = "Consent must be Y or N";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (loading) return;
    if (!validate()) return;

    try {
      setLoading(true);
      const response = await createEquifax(formData);
      setOpend(true);

      if (response?.status?.code === 200) {
        showSuccessToast(
          response?.status?.message || "Credit report generated successfully",
        );

        setReportData(response);

        const rawScore = response?.data?.score_detail?.[0]?.value;
        const score = Number(rawScore);
        if (!isNaN(score)) {
          setCreditScore(score);
        } else {
          setCreditScore(0);
        }
        setFormData((prev) => ({
          ...prev,
          name: "",
          mobile: "",
          pan: "",
          gender: "",
          dob: "",
          address: "",
          state: "",
          pincode: "",
          consent: "Y",
        }));

        setErrors({});
      } else {
        showErrorToast(response?.data?.status?.message || "Request failed");
        return;
      }
    } catch (err) {
      console.error(err);
      showErrorToast("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative ">
      {loading && (
        <div className="absolute inset-0 bg-white/70 flex items-center justify-center z-50">
          <Loader />
        </div>
      )}
      <form
        onSubmit={handleSubmit}
        className="bg-white p-2 rounded-md mt-2 shadow grid grid-cols-2 md:grid-cols-4 gap-2"
      >
        {/* Name */}
        <Input
          label="Customer Full Name"
          name="name"
          placeholder="Enter Full Name"
          value={formData.name}
          onChange={handleChange}
          error={errors.name}
          required
        />

        {/* Mobile */}
        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium">
            Mobile Number<span className="text-red-500">*</span>
          </label>
          <input
            name="mobile"
            value={formData.mobile}
            onChange={(e) => {
              const v = e.target.value.replace(/\D/g, "");
              if (v.length <= 10) {
                setFormData({ ...formData, mobile: v });
              }
            }}
            maxLength={10}
            inputMode="numeric"
            className="border rounded-lg px-4 py-1"
            placeholder="Mobile Number"
            required
          />

          {!isMobileValid && formData.mobile.length > 0 && (
            <span className="text-red-500 text-sm">
              Mobile number must be exactly 10 digits
            </span>
          )}
        </div>
        <div className="flex flex-col gap-1">
          <Input
            label="PAN"
            name="pan"
            placeholder="ABCDE1234F"
            value={formData.pan}
            onChange={handleChange}
            error={errors.pan}
            required
            maxLength={10}
          />

          {!isPanValid && formData.pan.length > 0 && (
            <span className="text-red-500 text-sm">
              PAN format is incorrect (ABCDE1234F)
            </span>
          )}
        </div>
        <div className="flex flex-col">
          <label className="text-sm font-medium mb-1">
            Gender <span className="text-red-500">*</span>
          </label>

          <select
            name="gender"
            value={formData.gender}
            onChange={handleChange}
            className={`border rounded p-2 text-sm ${
              errors.gender ? "border-red-500" : "border-gray-300"
            }`}
            required
          >
            <option value="" disabled>
              Select Gender
            </option>

            {GENDER_OPTIONS.map((g) => (
              <option key={g.value} value={g.value}>
                {g.label}
              </option>
            ))}
          </select>

          {errors.gender && (
            <span className="text-xs text-red-500 mt-1">{errors.gender}</span>
          )}
        </div>
        <Input
          label="DOB"
          type="date"
          name="dob"
          value={formData.dob}
          max={new Date().toISOString().split("T")[0]}
          min="1900-01-01"
          onChange={handleChange}
          error={errors.dob}
          required
        />
        <div className="flex flex-col">
          <label className="text-sm font-medium mb-1">
            State <span className="text-red-500">*</span>
          </label>

          <select
            name="state"
            value={formData.state}
            onChange={handleChange}
            className={`border rounded p-2 text-sm ${
              errors.state ? "border-red-500" : "border-gray-300"
            }`}
            required
          >
            <option value="">Select State</option>

            {states.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>

          {errors.state && (
            <span className="text-xs text-red-500 mt-1">{errors.state}</span>
          )}
        </div>
        <div className="flex flex-col gap-1">
          <Input
            label="Pincode"
            name="pincode"
            placeholder="Enter Pincode"
            value={formData.pincode}
            onChange={handleChange}
            error={errors.pincode}
            required
            maxLength={6}
            inputMode="numeric"
          />

          {!isPincodeValid && formData.pincode.length > 0 && (
            <span className="text-red-500 text-sm">
              Pincode must be exactly 6 digits
            </span>
          )}
        </div>
        <Input
          label="Address"
          name="address"
          placeholder="Enter Full Address"
          value={formData.address}
          onChange={handleChange}
          error={errors.address}
          required
        />
        <div className="col-span-2 md:col-span-4 text-center mt-2 w-full">
          <button
            type="submit"
            disabled={!isFormValid() || loading}
            className="px-10 py-2 bg-blue-700 text-white rounded hover:bg-blue-800 disabled:opacity-50"
          >
            {loading ? (
              <>
                <Loader size="sm" />
                Submitting...
              </>
            ) : (
              "Submit"
            )}
          </button>
        </div>
      </form>
      {open && (
        <div className="md:col-span-4 mt-6 flex flex-col items-center">
          <h3 className="text-2xl font-semibold mb-2">
            Hey {reportData?.data?.personal_information?.full_name || "N/A"} !!
          </h3>
          <h3 className="text-2xl font-semibold mb-2">
            Your Equifax Credit Score
          </h3>
          <div class="w-full md:w-[400px] relative md:mt-10">
            <span class="text-red-500 md:font-bold absolute md:top-[20px] md:left-10 md:mt-16 -rotate-70 top-[60px] left-5">
              Poor
            </span>
            <span class="text-yellow-400 md:font-bold absolute md:left-[104px] md:mt-1 -rotate-26 left-[69px] top-[-7px] ">
              Average
            </span>
            <span class="text-orange-600 md:font-bold absolute md:left-60 md:mt-0 md:rotate-20 left-[12rem] mt-[-9px] rotate-20">
              Good
            </span>
            <span class="text-green-800 md:font-bold absolute md:top-[20px] md:ml-[19.5rem] md:mt-16 rotate-70 ml-[16rem] mt-[3.5rem] ">
              Excellent
            </span>
            <GaugeChart
              id="gauge-chart1"
              nrOfLevels={3}
              percent={creditScore / maxScore}
              needleColor={"#00008b"}
              textColor={"transparent"}
              needleBaseColor={"red"}
              arcsLength={[0.3, 0.3, 0.3, 0.3]}
              colors={["#cc0000", "#e6e600", "#cc6600", "#009900"]}
            />
            <p
              class="text-black font-bold text-xl md:text-2xl absolute bottom-12  md:bottom-[5.5rem] 
                    left-1/2 transform -translate-x-1/2"
            >
              {creditScore}
            </p>
          </div>

          <p className="mt-2 text-sm font-medium">
            Score: <span className="font-bold">{creditScore}</span> / {maxScore}
          </p>
          <button
            type="button"
            onClick={() => {
              setShowReport(true);
            }}
            className={`px-4 py-2 rounded text-white transition
                                ${
                                  reportData
                                    ? "bg-green-600 hover:bg-green-700 cursor-pointer"
                                    : "bg-gray-400 cursor-not-allowed"
                                }`}
            disabled={!reportData}
          >
            View Credit Report
          </button>
        </div>
      )}
      {showReport && (
        <EquifaxReport
          states={states}
          data={reportData}
          onClose={() => setShowReport(false)}
        />
      )}
    </div>
  );
};

const Input = ({ label, error, required = false, ...props }) => (
  <div className="flex flex-col">
    <label className="text-sm font-medium mb-1">
      {label}
      {required && <span className="text-red-500 ml-1">*</span>}
    </label>

    <input
      {...props}
      className={`border rounded p-2 text-sm ${
        error ? "border-red-500" : "border-gray-300"
      }`}
    />

    {error && <span className="text-xs text-red-500 mt-1">{error}</span>}
  </div>
);

export default EquifaxForm;
